import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, PenLine, Plus, Send, CheckCheck } from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { formatDateLabel } from '@/lib/format';
import { useHolidays } from '@/hooks/useHolidays';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { DateLabel } from '@/components/DateLabel';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { buildLocalIso, isoToLocalHm, personKey as buildPersonKey, type PersonRef } from '@/lib/shift-format';
import {
  WorkRecordModal, StatusBadge,
  type WorkRecord, type WorkRecordModalTarget,
} from '@/components/WorkRecordModal';

/**
 * `/shift/records` (v0.4 追加要件⑮ 実装順序⑥、旧 `/shift/adjustments` を改称)。
 * 予定の部分変更に加え、**予定に無い勤務も記録できる** (D-516)。「予定どおり一括確定」
 * (`POST /work-records/bulk-from-plan`、D-534) により全員分の出退勤入力を強制しない。
 * 設計: docs/design/v04-planned-vs-actual-separation-detail.md §3-1/§4/§6 手順⑥
 *
 * ⚠️ 旧 `POST /shift/assignments/:id/adjustments` (`shift_actual_adjustments`) はここでは
 * 使わない。実績は必ず `POST /work-records` を経由する (実装順序③で読み手は全て work_records
 * へ切替済み)。
 *
 * ⚠️ 2026-09-09: 記録モーダル本体は `@/components/WorkRecordModal.tsx` へ切り出した
 * (日報 P-3 の `DailyReportWorkShiftsSection` と共有するため)。
 */

type Assignment = {
  assignment_id: string; work_date: string; start_at: string; end_at: string;
  break_minutes: number | null; status: string; person_name?: string | null;
  employee_id?: string | null; external_person_id?: string | null;
  home_node_id: string; work_node_id: string;
};

type ModalTarget = WorkRecordModalTarget;

// -----------------------------------------------------------------------------
// 自分の予定 (self scope)
// -----------------------------------------------------------------------------
const toDateOnly = (d: Date): string => d.toISOString().slice(0, 10);
const addDays = (yyyyMmDd: string, days: number): string => {
  const d = new Date(`${yyyyMmDd}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return toDateOnly(d);
};
const WINDOW_DAYS = 14;

function MyRecordsSection({ onCreate, onEdit }: {
  onCreate: (target: Extract<ModalTarget, { mode: 'create' }>) => void;
  onEdit: (record: WorkRecord) => void;
}) {
  const [anchor, setAnchor] = useState(() => toDateOnly(new Date()));
  const from = anchor;
  const to = addDays(anchor, WINDOW_DAYS - 1);
  const holidays = useHolidays(from, to);
  const queryClient = useQueryClient();

  const { data: assignData, isLoading } = useQuery<{ assignments: Assignment[] }>({
    queryKey: ['shift-my-for-records', from, to],
    queryFn: () => apiFetch(`/shift/my?from=${from}&to=${to}`),
  });
  const assignments = (assignData?.assignments ?? []).filter((a) => a.status !== 'cancelled');

  const { data: recordsData } = useQuery<{ work_records: WorkRecord[] }>({
    queryKey: ['work-records-self', from, to],
    queryFn: () => apiFetch(`/work-records?from=${from}&to=${to}`),
  });
  const recordByAssignment = new Map((recordsData?.work_records ?? []).filter((r) => r.planned_assignment_id).map((r) => [r.planned_assignment_id!, r]));

  const submitMut = useMutation({
    mutationFn: (workRecordId: string) => apiFetch(`/work-records/${workRecordId}/submit`, { method: 'POST' }),
    onSuccess: () => {
      toast.success('提出しました');
      queryClient.invalidateQueries({ queryKey: ['work-records-self'] });
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '提出に失敗しました'),
  });

  return (
    <div className="border border-surface-border rounded-lg bg-white p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-h2">自分の予定</h2>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" aria-label="前の期間" onClick={() => setAnchor((cur) => addDays(cur, -WINDOW_DAYS))}><ChevronLeft size={16} /></Button>
          <span className="text-xs text-text-sub tabular-nums">{formatDateLabel(from)}〜{formatDateLabel(to)}</span>
          <Button variant="ghost" size="sm" aria-label="次の期間" onClick={() => setAnchor((cur) => addDays(cur, WINDOW_DAYS))}><ChevronRight size={16} /></Button>
        </div>
      </div>
      {isLoading ? <div className="text-text-sub text-sm">読み込み中…</div> : assignments.length === 0 ? (
        <p className="text-sm text-text-sub">この期間に予定はありません。</p>
      ) : (
        <div className="divide-y divide-surface-border">
          {assignments.map((a) => {
            const record = recordByAssignment.get(a.assignment_id);
            return (
              <div key={a.assignment_id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                <div className="text-sm">
                  <span className="font-medium text-text-body"><DateLabel date={a.work_date} withWeekday holidays={holidays} /></span>
                  <span className="text-text-sub ml-2 tabular-nums">{isoToLocalHm(a.start_at)}〜{isoToLocalHm(a.end_at)}</span>
                </div>
                <div className="flex items-center gap-2">
                  {record && <StatusBadge status={record.status} />}
                  {!record && (
                    <Button variant="secondary" size="sm" leftIcon={<PenLine size={13} />}
                      onClick={() => onCreate({
                        mode: 'create', workDate: a.work_date, plannedAssignmentId: a.assignment_id,
                        homeNodeId: a.home_node_id, workNodeId: a.work_node_id, person: null,
                        defaultStartAt: a.start_at, defaultEndAt: a.end_at,
                      })}
                    >実績を記録</Button>
                  )}
                  {record && (record.status === 'draft' || record.status === 'rejected') && record.record_kind === 'worked' && (
                    <Button variant="ghost" size="sm" leftIcon={<PenLine size={13} />} onClick={() => onEdit(record)}>修正</Button>
                  )}
                  {record && record.status === 'draft' && (
                    <Button variant="secondary" size="sm" leftIcon={<Send size={13} />} loading={submitMut.isPending} onClick={() => submitMut.mutate(record.work_record_id)}>提出する</Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// 店舗の予定 (manager scope)
// -----------------------------------------------------------------------------
type Period = { period_id: string; label: string; start_date: string; end_date: string; status: string };
type PeriodNode = { node_id: string; node_name: string };
type BoardAssignment = Assignment & { employee_id: string | null; external_person_id: string | null; person_name?: string | null };

function StoreRecordsSection({ onCreate, onEdit }: {
  onCreate: (target: Extract<ModalTarget, { mode: 'create' }>) => void;
  onEdit: (record: WorkRecord) => void;
}) {
  const queryClient = useQueryClient();
  const { data: periodsData } = useQuery<{ periods: Period[] }>({ queryKey: ['shift-periods'], queryFn: () => apiFetch('/shift/periods') });
  const periods = periodsData?.periods ?? [];
  const [periodId, setPeriodId] = useState('');
  const activePeriodId = periodId || periods[0]?.period_id || '';
  const activePeriod = periods.find((p) => p.period_id === activePeriodId);

  const { data: nodesData } = useQuery<{ nodes: PeriodNode[] }>({
    queryKey: ['shift-period-nodes', activePeriodId],
    queryFn: () => apiFetch(`/shift/periods/${activePeriodId}/nodes`),
    enabled: !!activePeriodId,
  });
  const nodes = nodesData?.nodes ?? [];
  const [nodeId, setNodeId] = useState('');
  const activeNodeId = nodeId || nodes[0]?.node_id || '';
  const holidays = useHolidays(activePeriod?.start_date ?? '', activePeriod?.end_date ?? '', activeNodeId);

  const { data: boardData, isLoading, isError } = useQuery<{ assignments: BoardAssignment[] }>({
    queryKey: ['shift-board-for-records', activePeriodId, activeNodeId],
    queryFn: () => apiFetch(`/shift/periods/${activePeriodId}/nodes/${activeNodeId}/board`),
    enabled: !!activePeriodId && !!activeNodeId,
  });
  const assignments = (boardData?.assignments ?? []).filter((a) => a.status !== 'cancelled');

  const { data: recordsData, refetch: refetchRecords } = useQuery<{ work_records: WorkRecord[] }>({
    queryKey: ['work-records-node', activeNodeId, activePeriod?.start_date, activePeriod?.end_date],
    queryFn: () => apiFetch(`/work-records?node_id=${activeNodeId}&from=${activePeriod!.start_date}&to=${activePeriod!.end_date}`),
    enabled: !!activeNodeId && !!activePeriod,
  });
  const workRecords = recordsData?.work_records ?? [];
  const recordByAssignment = new Map(workRecords.filter((r) => r.planned_assignment_id).map((r) => [r.planned_assignment_id!, r]));

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['work-records-node'] });
    refetchRecords();
  };

  const bulkFromPlanMut = useMutation({
    mutationFn: (workDate: string) => apiFetch('/work-records/bulk-from-plan', {
      method: 'POST', body: JSON.stringify({ work_node_id: activeNodeId, work_date: workDate }),
    }),
    onSuccess: (res: unknown) => {
      const created = (res as { created?: string[] })?.created?.length ?? 0;
      toast.success(created > 0 ? `${created}件を予定どおり確定しました` : '確定できる予定はありませんでした');
      invalidate();
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '確定に失敗しました'),
  });

  const approveBulkMut = useMutation({
    mutationFn: (workRecordIds: string[]) => apiFetch('/work-records/approve-bulk', {
      method: 'POST', body: JSON.stringify({ work_record_ids: workRecordIds }),
    }),
    onSuccess: (res: unknown) => {
      const approved = (res as { approved?: string[] })?.approved?.length ?? 0;
      const skipped = (res as { skipped?: unknown[] })?.skipped?.length ?? 0;
      toast.success(`${approved}件を承認しました${skipped > 0 ? `（${skipped}件は承認できませんでした）` : ''}`);
      invalidate();
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '承認に失敗しました'),
  });

  const grouped = useMemo(() => {
    const map = new Map<string, BoardAssignment[]>();
    for (const a of assignments) {
      const list = map.get(a.work_date);
      if (list) list.push(a); else map.set(a.work_date, [a]);
    }
    return [...map.entries()].sort(([d1], [d2]) => d1.localeCompare(d2));
  }, [assignments]);

  // D-534: その日に「予定はあるが実績が未記録」の枠が1件でもあれば一括確定ボタンを出す
  const undoneCountByDate = useMemo(() => {
    const map = new Map<string, number>();
    for (const [date, list] of grouped) {
      map.set(date, list.filter((a) => !recordByAssignment.has(a.assignment_id)).length);
    }
    return map;
  }, [grouped, recordByAssignment]);

  const submittedIds = workRecords.filter((r) => r.status === 'submitted').map((r) => r.work_record_id);
  const submittedCount = submittedIds.length;

  // 対象者候補 (D-516 の「予定に無い勤務」用): この店舗案に登場する人物から選ぶ
  const candidatePeople = useMemo(() => {
    const map = new Map<string, { person: PersonRef; name: string }>();
    for (const a of assignments) {
      const key = a.employee_id ? `E:${a.employee_id}` : (a.external_person_id ? `X:${a.external_person_id}` : null);
      if (!key || map.has(key)) continue;
      map.set(key, { person: { employee_id: a.employee_id ?? null, external_person_id: a.external_person_id ?? null }, name: a.person_name ?? '(氏名不明)' });
    }
    return [...map.values()];
  }, [assignments]);
  const [unplannedPersonKey, setUnplannedPersonKey] = useState('');
  const [unplannedDate, setUnplannedDate] = useState('');

  return (
    <div className="border border-surface-border rounded-lg bg-white p-4 space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h2 className="text-h2">店舗の予定</h2>
        {submittedCount > 0 && (
          <Button variant="secondary" size="sm" leftIcon={<CheckCheck size={13} />}
            loading={approveBulkMut.isPending} onClick={() => approveBulkMut.mutate(submittedIds)}
          >承認待ち{submittedCount}件を一括承認</Button>
        )}
      </div>
      <div className="flex flex-wrap gap-3">
        <div className="w-64">
          <label className="block text-xs text-text-sub mb-1">シフト期間</label>
          <Select value={activePeriodId || undefined} onValueChange={setPeriodId}>
            <SelectTrigger className="w-full min-h-[44px]"><SelectValue placeholder="期間を選ぶ" /></SelectTrigger>
            <SelectContent>{periods.map((p) => <SelectItem key={p.period_id} value={p.period_id}>{p.label} ({p.start_date}〜{p.end_date})</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="w-56">
          <label className="block text-xs text-text-sub mb-1">店舗</label>
          <Select value={activeNodeId || undefined} onValueChange={setNodeId} disabled={!activePeriodId}>
            <SelectTrigger className="w-full min-h-[44px]"><SelectValue placeholder="店舗を選ぶ" /></SelectTrigger>
            <SelectContent>{nodes.map((n) => <SelectItem key={n.node_id} value={n.node_id}>{n.node_name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      {!activePeriodId || !activeNodeId ? (
        <p className="text-sm text-text-sub">閲覧できるシフト期間・店舗がありません。</p>
      ) : isLoading ? (
        <div className="text-text-sub text-sm">読み込み中…</div>
      ) : isError ? (
        <p className="text-sm text-text-sub">この店舗のシフトを閲覧する権限がありません。</p>
      ) : (
        <>
          {/* D-516: 予定に無い勤務も記録できる */}
          {candidatePeople.length > 0 && (
            <div className="flex flex-wrap items-end gap-2 border-t border-surface-border pt-3">
              <div className="w-48">
                <label className="block text-xs text-text-sub mb-1">対象者</label>
                <Select value={unplannedPersonKey || undefined} onValueChange={setUnplannedPersonKey}>
                  <SelectTrigger className="w-full min-h-[44px]"><SelectValue placeholder="選ぶ" /></SelectTrigger>
                  <SelectContent>{candidatePeople.map(({ person, name }) => (
                    <SelectItem key={buildPersonKey(person)} value={buildPersonKey(person)}>{name}</SelectItem>
                  ))}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-xs text-text-sub mb-1">勤務日</label>
                <input type="date" value={unplannedDate} onChange={(e) => setUnplannedDate(e.target.value)}
                  className="min-h-[44px] px-3 py-2 rounded border border-surface-border text-sm" />
              </div>
              <Button
                variant="secondary" size="sm" leftIcon={<Plus size={13} />}
                disabled={!unplannedPersonKey || !unplannedDate}
                onClick={() => {
                  const found = candidatePeople.find(({ person }) => buildPersonKey(person) === unplannedPersonKey);
                  if (!found) return;
                  onCreate({
                    mode: 'create', workDate: unplannedDate, plannedAssignmentId: null,
                    homeNodeId: activeNodeId, workNodeId: activeNodeId, person: found.person,
                    defaultStartAt: buildLocalIso(unplannedDate, '09:00'), defaultEndAt: buildLocalIso(unplannedDate, '17:00'),
                  });
                }}
              >予定に無い勤務を追加</Button>
            </div>
          )}

          {grouped.length === 0 ? (
            <p className="text-sm text-text-sub">この期間・店舗に割当がありません。</p>
          ) : (
            <div className="space-y-3">
              {grouped.map(([date, list]) => (
                <div key={date}>
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <div className="text-xs font-semibold text-text-sub"><DateLabel date={date} withWeekday holidays={holidays} /></div>
                    {(undoneCountByDate.get(date) ?? 0) > 0 && (
                      <Button variant="ghost" size="sm" loading={bulkFromPlanMut.isPending} onClick={() => bulkFromPlanMut.mutate(date)}>
                        この日は予定どおり一括確定
                      </Button>
                    )}
                  </div>
                  <div className="divide-y divide-surface-border border border-surface-border rounded">
                    {list.map((a) => {
                      const record = recordByAssignment.get(a.assignment_id);
                      return (
                        <div key={a.assignment_id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
                          <div className="text-sm">
                            <span className="font-medium text-text-body">{a.person_name ?? '(氏名不明)'}</span>
                            <span className="text-text-sub ml-2 tabular-nums">{isoToLocalHm(a.start_at)}〜{isoToLocalHm(a.end_at)}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            {record && <StatusBadge status={record.status} />}
                            {!record && (
                              <Button variant="secondary" size="sm" leftIcon={<PenLine size={13} />}
                                onClick={() => onCreate({
                                  mode: 'create', workDate: a.work_date, plannedAssignmentId: a.assignment_id,
                                  homeNodeId: a.home_node_id, workNodeId: a.work_node_id,
                                  person: { employee_id: a.employee_id, external_person_id: a.external_person_id },
                                  defaultStartAt: a.start_at, defaultEndAt: a.end_at,
                                })}
                              >実績を記録</Button>
                            )}
                            {record && (record.status === 'draft' || record.status === 'rejected') && record.record_kind === 'worked' && (
                              <Button variant="ghost" size="sm" leftIcon={<PenLine size={13} />} onClick={() => onEdit(record)}>修正</Button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export function ShiftRecordsPage() {
  const [target, setTarget] = useState<ModalTarget | null>(null);
  const queryClient = useQueryClient();

  const handleDone = () => {
    setTarget(null);
    queryClient.invalidateQueries({ queryKey: ['work-records-self'] });
    queryClient.invalidateQueries({ queryKey: ['work-records-node'] });
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-h1">勤務実績</h1>
        <p className="text-sm text-text-sub mt-1">シフト通りが既定です。予定と違った勤務や、予定に無い勤務があった枠だけ記録してください。</p>
      </div>
      <MyRecordsSection onCreate={setTarget} onEdit={(r) => setTarget({ mode: 'edit', record: r })} />
      <StoreRecordsSection onCreate={setTarget} onEdit={(r) => setTarget({ mode: 'edit', record: r })} />
      {target && <WorkRecordModal target={target} onClose={() => setTarget(null)} onDone={handleDone} />}
    </div>
  );
}
